/* Motion extras for the modern pages. Five small, separate jobs; each does nothing if its hooks are missing,
 * and each is skipped for people who ask for reduced motion. The pre-animation state is always applied by
 * this script, never by the stylesheet, so if the script fails the page is simply still - nothing is left
 * invisible or out of place. (Same rule as assets/ms-fit-liquid.js.)
 *
 * 1. Image fade-in: product photos that are still downloading fade in when they arrive instead of popping.
 * 2. Scroll reveals on the homepage: section headings, offers, category tiles and the first cards of each
 *    product row rise slightly as they come into view. Only for things below the first screen, once.
 * 3. Card to product page: tags the tapped card's photo so the browser can carry it across to the product
 *    page (cross-document view transitions; see mstore-modern-core.css). Unsupported browsers just navigate.
 * 4. Collection grid: a short fade when the filtered grid is rebuilt.
 * 5. Swipe to close: drag the cart drawer to the right, or the quick-add sheet down by its top, to dismiss.
 *    A flick counts as well as a long drag; a short one springs back.
 * 6. Header on phones: hides while scrolling down, returns on scroll up.
 */
(() => {
  if (window.__msMotion) return;
  window.__msMotion = true;

  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const EASE_OUT = 'cubic-bezier(0.23, 1, 0.32, 1)';

  /* 1. Image fade-in ------------------------------------------------------------------------------- */
  const FADE_IMAGES = 'img.ms-card__image, .mstore-pdp__thumb img, .ms-card__swatch img, .ms-quick-add__chip img, .mstore-pdp__media-panel img[loading="lazy"], .ms-category img';
  const watchImage = (img) => {
    if (img.__msFade || img.complete) return;
    img.__msFade = true;
    img.classList.add('ms-img-loading');
    const done = () => img.classList.remove('ms-img-loading');
    img.addEventListener('load', done, { once: true });
    img.addEventListener('error', done, { once: true });
  };
  const watchImagesIn = (root) => {
    if (!root.querySelectorAll) return;
    if (root.matches && root.matches(FADE_IMAGES)) watchImage(root);
    root.querySelectorAll(FADE_IMAGES).forEach(watchImage);
  };

  /* 2. Scroll reveals ------------------------------------------------------------------------------ */
  const REVEAL_GROUPS = [
    { each: '.ms-section__head' },
    { parent: '.ms-offers__grid', limit: 4 },
    { parent: '.ms-categories', limit: 4 },
    { parent: '.ms-product-grid--showcase', limit: 4 }
  ];
  const setupReveals = () => {
    if (reduceMotion() || !('IntersectionObserver' in window)) return;
    if (!document.body.classList.contains('template-index')) return;
    const fold = window.innerHeight;
    const pending = new Map();
    const show = (trigger) => {
      const group = pending.get(trigger);
      if (!group) return;
      pending.delete(trigger);
      observer.unobserve(trigger);
      group.forEach((el, index) => {
        el.style.transition = `opacity 420ms ${EASE_OUT} ${index * 50}ms, transform 420ms ${EASE_OUT} ${index * 50}ms`;
        el.style.opacity = '';
        el.style.transform = '';
        setTimeout(() => { el.style.transition = ''; el.style.willChange = ''; }, 520 + index * 50);
      });
    };
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => { if (entry.isIntersecting) show(entry.target); });
    }, { threshold: 0.15 });
    /* Safety net. Content must never stay hidden because an observer did not fire (a tab restored in the
       background, an embedded browser that pauses rendering). Every second and a half, anything still
       waiting that is on screen is shown; the timer stops once nothing is waiting. */
    const watchdog = setInterval(() => {
      if (!pending.size) { clearInterval(watchdog); return; }
      Array.from(pending.keys()).forEach((trigger) => {
        const rect = trigger.getBoundingClientRect();
        if (rect.top < window.innerHeight && rect.bottom > 0) show(trigger);
      });
    }, 1500);

    const prepare = (trigger, items) => {
      if (!items.length || trigger.getBoundingClientRect().top < fold) return;
      if (trigger.closest('.ms-fit')) return;
      items.forEach((el) => { el.style.opacity = '0'; el.style.transform = 'translateY(14px)'; el.style.willChange = 'opacity, transform'; });
      pending.set(trigger, items);
      observer.observe(trigger);
    };
    REVEAL_GROUPS.forEach((group) => {
      if (group.each) document.querySelectorAll(group.each).forEach((el) => prepare(el, [el]));
      else document.querySelectorAll(group.parent).forEach((parent) => prepare(parent, Array.from(parent.children).slice(0, group.limit)));
    });
  };

  /* 3. Card to product page ------------------------------------------------------------------------ */
  const clearTransitionNames = () => {
    document.documentElement.classList.remove('ms-vt-leaving');
    document.querySelectorAll('[data-ms-vt]').forEach((el) => { el.style.viewTransitionName = ''; el.removeAttribute('data-ms-vt'); });
  };
  document.addEventListener('click', (event) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = event.target.closest ? event.target.closest('.ms-card__link') : null;
    if (!link) return;
    const image = link.querySelector('.ms-card__image--primary') || link.querySelector('.ms-card__image');
    if (!image) return;
    clearTransitionNames();
    document.documentElement.classList.add('ms-vt-leaving');
    image.setAttribute('data-ms-vt', '');
    image.style.viewTransitionName = 'ms-product';
  });
  window.addEventListener('pageshow', clearTransitionNames);

  /* 4. Collection grid ----------------------------------------------------------------------------- */
  const watchGrid = () => {
    const grid = document.querySelector('.ms-product-grid:not(.ms-product-grid--showcase)');
    if (!grid || !('MutationObserver' in window)) return;
    let last = 0;
    new MutationObserver((mutations) => {
      const added = mutations.reduce((sum, m) => sum + m.addedNodes.length, 0);
      const now = Date.now();
      if (added < 4 || now - last < 600 || reduceMotion()) return;
      last = now;
      grid.classList.remove('is-refreshed');
      void grid.offsetWidth;
      grid.classList.add('is-refreshed');
    }).observe(grid, { childList: true });
  };

  /* 5. Swipe to close ------------------------------------------------------------------------------ */
  const FLICK = 0.11; // px per ms
  let drag = null;
  const sheetMode = () => window.matchMedia('(max-width: 749px)').matches;
  const startDrag = (event) => {
    if (event.pointerType === 'mouse' || drag) return;
    const target = event.target;
    if (!target || !target.closest) return;
    const drawer = target.closest('.ms-cart-drawer.is-open');
    if (drawer) {
      if (target.closest('.ms-cart-addon__list, input, textarea, select')) return;
      drag = { el: drawer, axis: 'x', x: event.clientX, y: event.clientY, t: Date.now(), active: false, id: event.pointerId };
      return;
    }
    const head = target.closest('.ms-quick-add__head');
    if (head && sheetMode() && !target.closest('button, a, input')) {
      const dialog = head.closest('dialog.ms-quick-add');
      if (dialog && dialog.open) drag = { el: dialog, axis: 'y', x: event.clientX, y: event.clientY, t: Date.now(), active: false, id: event.pointerId };
    }
  };
  const moveDrag = (event) => {
    if (!drag || event.pointerId !== drag.id) return;
    const dx = event.clientX - drag.x;
    const dy = event.clientY - drag.y;
    if (!drag.active) {
      if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
      const along = drag.axis === 'x' ? dx : dy;
      const across = drag.axis === 'x' ? dy : dx;
      if (along <= 0 || Math.abs(across) > Math.abs(along)) { drag = null; return; }
      drag.active = true;
      drag.el.style.transition = 'none';
    }
    const distance = Math.max(0, drag.axis === 'x' ? dx : dy);
    drag.distance = distance;
    drag.el.style.transform = drag.axis === 'x' ? `translateX(${distance}px)` : `translateY(${distance}px)`;
  };
  const endDrag = (event) => {
    if (!drag || (event && event.pointerId !== drag.id)) return;
    const current = drag;
    drag = null;
    if (!current.active) return;
    const el = current.el;
    const distance = current.distance || 0;
    const size = current.axis === 'x' ? el.offsetWidth : el.offsetHeight;
    const speed = distance / Math.max(1, Date.now() - current.t);
    const dismiss = event && event.type === 'pointerup' && (distance > size * 0.3 || (speed > FLICK && distance > 60));
    el.style.transition = '';
    if (dismiss) {
      if (current.axis === 'x') { if (window.MStoreCart) window.MStoreCart.close(); else el.classList.remove('is-open'); }
      else if (el.open) el.close();
    }
    // Clearing the inline transform hands the element back to its stylesheet transition, which runs from
    // where the finger left it: out to the closed position, or back to rest.
    el.style.transform = '';
  };
  document.addEventListener('pointerdown', startDrag, { passive: true });
  document.addEventListener('pointermove', moveDrag, { passive: true });
  document.addEventListener('pointerup', endDrag, { passive: true });
  document.addEventListener('pointercancel', endDrag, { passive: true });

  /* 6. Header on phones ---------------------------------------------------------------------------- */
  /* Slides away on the way down the page and returns on the first scroll up. One passive scroll listener,
     read once per frame; it only toggles a class, the stylesheet does the moving. Never tucks near the top
     of the page or while a drawer, menu or sheet holds the page locked. */
  const setupHeader = () => {
    const wrapper = document.querySelector('.ms-header-section');
    if (!wrapper) return;
    const phone = window.matchMedia('(max-width: 760px)');
    let lastY = window.scrollY;
    let queued = false;
    const update = () => {
      queued = false;
      const y = window.scrollY;
      const delta = y - lastY;
      if (!phone.matches || document.body.classList.contains('ms-lock') || y < 120) {
        wrapper.classList.remove('is-tucked');
        lastY = y;
        return;
      }
      if (Math.abs(delta) < 6) return;
      wrapper.classList.toggle('is-tucked', delta > 0);
      lastY = y;
    };
    window.addEventListener('scroll', () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(update);
    }, { passive: true });
  };

  /* Start ------------------------------------------------------------------------------------------ */
  const init = () => {
    watchImagesIn(document);
    if ('MutationObserver' in window) {
      new MutationObserver((mutations) => {
        mutations.forEach((m) => m.addedNodes.forEach((node) => { if (node.nodeType === 1) watchImagesIn(node); }));
      }).observe(document.body, { childList: true, subtree: true });
    }
    setupReveals();
    watchGrid();
    setupHeader();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
